import { useContext, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import _ from 'lodash';
import Breadcrumb from 'components/atoms/Breadcrumb';
import { findAttributeTypeFromList, getConfig, sendNotification, convertArraysToSets } from 'utils/helperMethods';
import { useNavigate, useParams } from 'react-router-dom';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import InputDisplay from 'components/atoms/DisplayName/input-display';
import { ConfigKeys, priceAnalyticsErrorMessages, pricingSimulatorErrorMessages } from 'utils/Enums';

import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingAnalyticsUrls } from 'config/urls';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { tabsType } from 'types/pricingSimulator';
import TabSwitchComponent from 'components/atoms/TabSwitch/TabSwitch';
import ScenarioAnalyticsContainer from 'components/molecules/PricingSimulator/Analytics/ScenarioAnalyticsContainer';
import ScenarioComparisonContainer from 'components/molecules/PricingSimulator/Analytics/ScenarioComparisonContainer';
import BaseScenarioSelectionModal from 'components/molecules/PricingSimulator/Modals/BaseScenarioSelectionModal';
import dayjs from 'dayjs';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

const UpdateOrViewPricingAnalytics = () => {
    const { mode, id, reportId: reportIdFromUrl } = useParams();
    // TODO :  Analytics tab switch

    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const dim1DropdownList = dimensionConfigData?.dim1DropdownList ?? [];
    const dim2DropdownList = dimensionConfigData?.dim2DropdownList ?? [];
    const defaultDim1DropdownItem = dimensionConfigData?.defaultDim1DropdownItem ?? {
        value: '',
        label: '',
        dimension: '',
    };
    const defaultDim2DropdownItem = dimensionConfigData?.defaultDim2DropdownItem ?? {
        value: '',
        label: '',
        dimension: '',
    };

    const { categoryMovementKpis } = pricingSimulatorConfig;

    const { openScenarioPopup, setOpenScenarioPopup } = useContext(PricingAnalyticsDataContext);
    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, allDimAndAttributeType, timePeriodLoading } =
        useContext(FilterContextData);
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );
    const navigate = useNavigate();
    const [tempName, setTempName] = useState(''); // to track new scenario name

    //tabs
    const analyticsTabs: tabsType[] = [
        {
            key: 'analytics',
            label: 'Scenario Analytics',
            headerLabel: 'Scenario Analytics',
            component: <ScenarioAnalyticsContainer />,
        },
        {
            key: 'comparison',
            label: 'Scenario Comparison',
            headerLabel: 'Scenario Comparison',
            component: <ScenarioComparisonContainer />,
        },
    ];
    const [activeTab, setActiveTab] = useState<tabsType>(analyticsTabs[0]);

    const {
        breadcrumbItems,
        selectedReportInfo,
        pricingAnalyticsIndexScreenUrl,
        updateReportProperty,
        updateComparisonProperty,
        resetFilterObject,
        scenarioComparisonState,
        setAllCheckedAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        setShowIsEditedRecords,
        loggedInUser,
        isUpdate,
        hasReportAccess,
        reportAccessLoading,
    } = useContext(PricingAnalyticsDataContext);

    const isNameAlreadyExists = useMemo(() => {
        return false;
    }, []);
    //dropdown list
    const listToDisplay = useMemo(() => {
        return [
            { label: 'Product', options: dim1DropdownList },
            { label: 'Geo', options: dim2DropdownList },
        ];
    }, [dim1DropdownList, dim2DropdownList]);

    //get single report details
    const { loading: singleReportDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {
            enabled:
                Boolean(hasReportAccess) &&
                Boolean(dim1DropdownList.length > 0) &&
                Boolean(enabledVariable) &&
                activeTab?.key === 'analytics',
        },
        queryKey: ['pa-single-report-details', reportIdFromUrl],
        url: `${pricingAnalyticsUrls.getReportMetaDataUrl}/${reportIdFromUrl}`,
        variables: {},
        parserFunction: (res: any) => {
            //redirect user to index page if error is thrown
            if (!res || res?.error || Object.keys(res).length === 0) {
                sendNotification('error', 'Report does not exist.');
                navigate(pricingAnalyticsIndexScreenUrl);
                throw 'No Data';
            }
            updateReportProperty('isPrivate', res?.is_private);
            updateReportProperty('reportId', Number(reportIdFromUrl));
            updateReportProperty('reportName', res?.report_name);
            updateReportProperty('createdBy', res?.created_by);
            updateReportProperty('updatedAt', dayjs.unix(res?.updated_at).fromNow());
            updateReportProperty('baseScenario', {
                scenarioId: res?.base_scenario_id,
                scenarioName: res?.base_scenario_name,
                status: res?.base_scenario_status ?? 'DRAFT',
                isPrivate: res?.base_scenario_visibility ?? false,
            });
            const changeAnalyticsOutputData: any[] = [];

            updateReportProperty('baseKPIs', {
                selectedKPIs: res?.global_kpi_ids, // selected kpis
            });

            const changeAnalyticsInputData = res?.change_analytics_metadata?.map((item: any) => {
                //find the selected level item from list
                const attributeTypeObj = findAttributeTypeFromList(listToDisplay, item?.attribute_type);
                changeAnalyticsOutputData.push({ data: [] });
                return {
                    attributeType: attributeTypeObj,
                    graphId: item?.graph_id,
                    kpiId: item?.kpi_id,
                };
            });
            const categoryMovementOutputData: any[] = [];
            const categoryMovementInputData = res?.category_movement_metadata?.map((item: any, index: number) => {
                //find the selected level item from list
                const attributeTypeObj = findAttributeTypeFromList(listToDisplay, item?.attribute_type);
                categoryMovementOutputData.push({ data: [] });
                return {
                    bottomChartAttributeType: attributeTypeObj,
                    bottomChartKpiId: categoryMovementKpis?.[index]?.value,
                };
            });
            updateReportProperty('changeAnalytics', {
                input: changeAnalyticsInputData,
                output: changeAnalyticsOutputData,
            });

            updateReportProperty('categoryMovement', {
                input: categoryMovementInputData,
                output: {
                    topChartItems: categoryMovementOutputData,
                    bottomChartItems: categoryMovementOutputData,
                },
            });

            if (res?.filters === '{}' || Object.keys(res?.filters).length === 0) {
                resetFilterObject();
            } else {
                const parsedData = convertArraysToSets(res.filters);
                setAllCheckedAttribute(parsedData?.allCheckedAttribute);
                setAllSelectedValueForAllAttribute(parsedData?.allSelectedValueForAllAttribute);
                setShowIsEditedRecords(parsedData?.showIsEditedRecords);
                setApplyFilterObject({ ...parsedData?.applyFilterObject });
            }

            return {};
        },
    });

    //get single comparison meta details
    const { loading: singleComparisonDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {
            enabled:
                Boolean(dim1DropdownList.length > 0) && Boolean(enabledVariable) && activeTab?.key === 'comparison',
        },
        queryKey: ['pa-comparison-details', reportIdFromUrl],
        url: `${pricingAnalyticsUrls.getComparisonMetaDataUrl}/${reportIdFromUrl}`,
        variables: {},
        parserFunction: (res: any) => {
            //redirect user to index page if error is thrown
            if (!res || res?.error || Object.keys(res).length === 0) {
                sendNotification('error', 'Report does not exist.');
                navigate(pricingAnalyticsIndexScreenUrl);
                throw 'No Data';
            }
            //find the selected level item from list
            const attributeTypes = res?.heatmap_details?.attribute_types?.map((item: string) => {
                const attributeTypeObj = findAttributeTypeFromList(listToDisplay, item);
                return attributeTypeObj;
            });

            //only add kpi if its present in list
            if (selectedReportInfo?.baseKPIs?.selectedKPIs.includes(res?.selected_kpi_item?.kpi_id)) {
                //update selected kpi
                updateComparisonProperty('selectedKpiItem', {
                    id: res?.selected_kpi_item?.kpi_id,
                    label: res?.selected_kpi_item?.kpi_name,
                    pre: res?.selected_kpi_item?.pre,
                    post: res?.selected_kpi_item?.post,
                });
            } else {
                updateComparisonProperty('selectedKpiItem', undefined);
            }

            updateComparisonProperty('tableState', {
                ...scenarioComparisonState?.tableState,
                offset: 0,
                search: '',
                // only when dim attributes empty add default attributes
                dimAttributes:
                    attributeTypes?.length > 0 ? attributeTypes : [defaultDim1DropdownItem, defaultDim2DropdownItem],
                sortObject: {
                    sortBy:
                        attributeTypes?.length > 0 ? attributeTypes?.[0]?.value : defaultDim1DropdownItem?.value ?? '',
                    sortingOrder: 'asc',
                    sortType: 'actual',
                },
            });

            updateComparisonProperty('selectedScenarios', [
                ...res?.scenario_details_list?.map((item: any) => {
                    return {
                        scenarioName: item.scenario_name,
                        scenarioId: item?.scenario_id,
                        status: item?.scenario_status,
                        isPrivate: item?.is_private,
                    };
                }),
            ]);
            return {};
        },
    });

    const mainSectionHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: 'report-details-main-section',
        height: mainSectionHeight.current,
        buffer: 70, //for padding at the bottom, change to 16 once testing is done
        // triggerDummyRender: true,
    });

    //update report name api
    const { mutate: updateReportNameMutation, loading: updateReportNameLoading } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.updateReportNameUrl,
        method: 'post',
    });

    const updateScenarioNameHandler = (updatedName: string) => {
        const payload = {
            report_id: selectedReportInfo?.reportId,
            report_name: updatedName,
            user_name: loggedInUser,
        };

        if (updatedName !== selectedReportInfo?.reportName) {
            updateReportNameMutation(
                { ...payload },
                {
                    onSuccess: (data) => {
                        updateReportProperty('reportName', updatedName);
                    },
                    onError: (err: any) => {
                        sendNotification('error', err.response?.data?.detail);
                    },
                },
            );
        } else {
            // temp name for handling change
            updateReportProperty('reportName', 'Temp Name');
            setTimeout(() => {
                updateReportProperty('reportName', updatedName);
            });
        }
    };

    const commonMainLoader =
        allDimAndAttributeType?.loading ||
        singleReportDetailsLoading ||
        reportAccessLoading ||
        singleComparisonDetailsLoading;

    const { loading: commonLoading } = useDebounceLoading({
        loadingDependency: commonMainLoader,
    });

    const activeTabComponent = useMemo(() => {
        return analyticsTabs.filter((item) => item.key === activeTab.key)?.[0]?.component;
    }, [activeTab]);
    return (
        <div id="report-details-main-section">
            {commonLoading ? (
                <div
                    style={{ height: mainSectionHeight.current ?? 'auto' }}
                    className="bg-white flex justify-center items-center"
                >
                    <Spin />
                </div>
            ) : (
                <section id="pricing-analytics-update-view-page">
                    <div className="flex items-baseline justify-between gap-4">
                        <Breadcrumb style={{ marginBottom: 10, flexShrink: 0 }} items={breadcrumbItems} />
                    </div>
                    {/* ANALYTICS/COMPARISON MAIN SECTION */}
                    <div
                        id="analytics-main-section"
                        className="border border-borderLinePurpuleAlt p-4 rounded bg-white h-full relative flex flex-col"
                    >
                        {/* 1. Name and switch component and actions buttons  */}
                        <div className="flex justify-between border-b pb-2 border-borderGreyAlt items-center">
                            <div className="font-sm font-medium flex gap-2 items-center text-blackAlt">
                                <InputDisplay
                                    inputValue={selectedReportInfo?.reportName || 'Report Name'}
                                    defaultDisplayName={selectedReportInfo?.reportName || 'Report Name'}
                                    maxCharacter={50}
                                    maxLengthValidationMessage={
                                        pricingSimulatorErrorMessages.MAX_LENGTH_PRICING_ANALYTICS
                                    }
                                    minLengthValidationMessage={priceAnalyticsErrorMessages.MIN_LENGTH}
                                    alreadyExistMessage={priceAnalyticsErrorMessages.DUPLICATE_NAME}
                                    alreadyExist={isNameAlreadyExists}
                                    disable={!isUpdate}
                                    minCharacter={3}
                                    onApplyHandler={(updatedName: string) => {
                                        updateScenarioNameHandler(updatedName);
                                    }}
                                    textChangeHandler={(value: string) => setTempName(value)}
                                    showLoader={updateReportNameLoading}
                                    disableBlur
                                />

                                <TabSwitchComponent
                                    activeTabKey={activeTab?.key}
                                    onTabChange={function (tab: tabsType): void {
                                        setActiveTab(tab);
                                    }}
                                    tabs={analyticsTabs}
                                />
                            </div>

                            {isUpdate && (
                                <div className="flex gap-2 items-center justify-center">
                                    <div
                                        onClick={() => {
                                            setOpenScenarioPopup(true);
                                        }}
                                        className="rounded cursor-pointer bg-purpleFont hover:text-white text-white  py-1 px-4  flex-grow"
                                    >
                                        {activeTab?.key === 'analytics' ? 'Update Base Scenario' : 'Select Scenarios'}
                                    </div>
                                </div>
                            )}
                        </div>
                        {activeTabComponent}
                    </div>
                    <BaseScenarioSelectionModal
                        mode={activeTab?.key === 'comparison' ? 'comparison' : 'analytics'}
                        from="edit"
                        open={openScenarioPopup}
                        onCancel={(e: any) => {
                            setOpenScenarioPopup(false);
                        }}
                        onSubmitClick={(data: any) => {
                            //  onEditApply(data);
                        }}
                    />
                </section>
            )}
        </div>
    );
};

export default UpdateOrViewPricingAnalytics;
